import Checkbox from '@atlaskit/checkbox';
import { key, subscribeTo } from '@databases/ping-registry';
import { getService } from '@databases/service-container';
import React, { useEffect, useState } from 'react';

interface SelectionsEntryPrefixContentProps {
  entryId: string;
  index: number;
  shouldRenderCheckbox: (isSelected: boolean) => boolean;
}

const preventCheckboxMouseFocus = (event: React.MouseEvent<HTMLInputElement>) => {
  event.preventDefault();
};

/**
 * Renders the content of the entry prefix cell when the `databasesSelectionsPerformance`
 * experiment is enabled.
 *
 * This component owns the row-level ping subscription and decides whether to show the
 * row number or the selection checkbox — logic that previously lived in `EntryPrefix`
 * via `shouldRenderCheckbox`. By keeping the subscription here (in a lightweight component)
 * rather than in the heavier `EntryPrefix`, re-renders triggered by selection changes
 * are cheap and fast.
 *
 * Rendering rules:
 * - Shows the row number when: not hovered, not selected, and numbered entries are on
 * - Shows the checkbox otherwise (hovered, selected, or numbered entries are off)
 *
 * Input events on the checkbox are captured by the selections package effect.
 */
const SelectionsEntryPrefixContent = ({
  entryId,
  index,
  shouldRenderCheckbox
}: SelectionsEntryPrefixContentProps) => {
  const selectionsApi = getService('SelectionsApi');
  const [isChecked, setIsChecked] = useState(() => selectionsApi.isRowSelected(index));

  useEffect(() => {
    const rowKey = key('row', { entryId });

    return subscribeTo(rowKey, 'selected', (selected: boolean) => {
      setIsChecked(selected);
    });
  }, [entryId]);

  if (!shouldRenderCheckbox(isChecked)) {
    return <>{index + 1}</>;
  }

  return (
    <Checkbox
      className="selections-entry-checkbox"
      isChecked={isChecked}
      data-entry-id={entryId}
      data-row-index={index}
      onMouseDown={preventCheckboxMouseFocus}
    />
  );
};

export default SelectionsEntryPrefixContent;
